import { cn } from "@bcc/lib/utils";
import { generateTestId } from "@client-shared";
import { useTranslation } from "react-i18next";
import type { CatalogueField, CatalogueFieldCoverage } from "./admin-catalogue-rows";

export const setAdminCatalogueMetricTestId = (field: CatalogueField): string => generateTestId("AdminCatalogueMetrics", field);

export type CoverageTone = "complete" | "partial" | "none";

export const coverageTone = ({ present, total }: CatalogueFieldCoverage): CoverageTone => {
  if (total > 0 && present === total) return "complete";
  return present > 0 ? "partial" : "none";
};

const TONE_CLASSES: Record<CoverageTone, string> = {
  complete: "border-green-200 bg-green-50 text-green-900",
  partial: "border-amber-200 bg-amber-50 text-amber-900",
  none: "border-red-200 bg-red-50 text-red-900",
};

interface AdminCatalogueMetricsProps {
  readonly coverage: CatalogueFieldCoverage[];
  readonly activeMissing: readonly CatalogueField[];
  readonly onToggleMissing: (field: CatalogueField) => void;
}

/**
 * Field coverage across the catalogue's parent products. Each metric is also
 * a shortcut that toggles the "missing this field" filter.
 */
export function AdminCatalogueMetrics({ coverage, activeMissing, onToggleMissing }: AdminCatalogueMetricsProps) {
  const { t } = useTranslation();

  return (
    <fieldset className="mx-0 flex min-w-0 flex-wrap gap-3 border-0 p-0">
      <legend className="sr-only">{t("admin.productCatalogue.metrics.label")}</legend>
      {coverage.map((metric) => {
        const isActive = activeMissing.includes(metric.field);
        const fieldLabel = t(`admin.productCatalogue.metrics.${metric.field}`);
        return (
          <button
            key={metric.field}
            type="button"
            data-testid={setAdminCatalogueMetricTestId(metric.field)}
            data-tone={coverageTone(metric)}
            aria-pressed={isActive}
            aria-label={t("admin.productCatalogue.metrics.toggleMissing", {
              field: fieldLabel,
              present: metric.present,
              total: metric.total,
            })}
            onClick={() => onToggleMissing(metric.field)}
            className={cn(
              "flex items-baseline gap-2 rounded-lg border px-4 py-2 text-xs font-medium uppercase tracking-wide transition-shadow",
              TONE_CLASSES[coverageTone(metric)],
              isActive && "ring-2 ring-primary ring-offset-1"
            )}
          >
            <span>{fieldLabel}</span>
            <span className="text-base font-semibold">{metric.present}</span>
            <span className="opacity-70">/ {metric.total}</span>
          </button>
        );
      })}
    </fieldset>
  );
}
